import Vue from "vue";
import Vuex from "vuex";
import { httpHelper } from "./utils/httpHelper.js";

Vue.use(Vuex);

export default new Vuex.Store({
	state: {
		userIsLoggedIn: false,
		otpSent: false,
		username: "",
		userEmail: "",
		organisationName: "",
		activeWorkflow: null,
		selectedWorkflowVersions: JSON.parse(
			localStorage.getItem("workflow_selected_versions") || "{}"
		),
		referencePath: { path: "" },
		lastFocusedVariableInsertionInput: null,
		unsavedChangesPresent: false,
		inputsOutputsForNode: null,
		authToken: null,
		authTokenExpires: null,
		changesToUsageCredit: true,
		changesToNodeSettings: null,
		showStickyNotes: false,
		stickyNoteDescription: "",
		stickyNoteMatches: [],
		stickyNotesLoading: false,
		stickyNoteTotal: 0,
		canvasZoom: 1,
		walletBalanceGBP: null,
		isAdmin: false
	},
	getters: {
		isLoggedIn(state) {
			return state.userIsLoggedIn;
		},
		isOtpSent(state) {
			return state.otpSent;
		},
		getLoginEmail(state) {
			return state.userEmail;
		},
		returnActiveWorkflow(state) {
			return state.activeWorkflow;
		},
		returnReferencePath(state) {
			return state.referencePath;
		},
		returnLastFocusedVariableInsertionInput(state) {
			return state.lastFocusedVariableInsertionInput;
		},
		returnUnsavedChangesPresent(state) {
			return state.unsavedChangesPresent;
		},
		returnInputsOutputsForNode(state) {
			return state.inputsOutputsForNode;
		},
		returnChangesToUsageCredit(state) {
			return state.changesToUsageCredit;
		},
		returnChangesToNodeSettings(state) {
			return state.changesToNodeSettings;
		},
		getSelectedWorkflowVersion: state => workflowId => state.selectedWorkflowVersions[workflowId] || null,
		showStickyNotes(state) {
			return state.showStickyNotes;
		},
		getStickyNoteDescription(state) {
			return state.stickyNoteDescription;
		},
		getStickyNoteMatches(state) {
			return state.stickyNoteMatches;
		},
		isStickyNotesLoading(state) {
			return state.stickyNotesLoading;
		},
		getStickyNoteTotal(state) {
			return state.stickyNoteTotal;
		},
		getWalletBalance(state) {
			return state.walletBalanceGBP;
		},
		isAdmin(state) {
			return state.isAdmin;
		}
	},
	actions: {
		async refreshTokenMETHOD({ commit }) {
			const auth = await httpHelper("RUNTIME", "POST", "security/refreshTokens", {
				refreshToken: Vue.$cookies.get("refreshToken"),
			});
			if (!auth?.error) {
				const { tokens, user } = auth.response;
				Vue.$cookies.set("refreshToken", tokens.refresh.token, tokens.refresh.expires);
				Vue.$cookies.set("refreshTokenExpiry", tokens.refresh.expires, tokens.refresh.expires);
				Vue.$cookies.set("accessToken", tokens.access.token, tokens.access.expires);
				Vue.$cookies.set("accessTokenExpiry", tokens.access.expires, tokens.access.expires);
				Vue.$cookies.set("username", user.username, "60d");
				Vue.$cookies.set("orgName", user.organisationName, "60d");
				if (user?.workflowTourStatus) {
					Vue.$cookies.set("workflowTourStatus", user.workflowTourStatus, "60d");
				}
				commit("logIn", { tokens, user });
			} else {
				commit("logOut");
				throw Error("Refresh token operation failed");
			}
		},
		async loginMETHOD({ commit }, data) {
			const auth = await httpHelper("RUNTIME", "POST", "security/login", data || {});
			if (!auth.error) {
				const { otpSent, user } = auth.response;
				Vue.$cookies.set("email", user.email, "60d");
				Vue.$cookies.set("username", user.username, "60d");
				Vue.$cookies.set("orgName", user.organisationName, "60d");
				if (user?.workflowTourStatus) {
					Vue.$cookies.set("workflowTourStatus", user.workflowTourStatus, "60d");
				}
				commit("logIn", { otpSent, user });
			} else {
				commit("logOut");
				return auth;
			}
		},
		async login2FAMETHOD({ commit }, data) {
			const auth = await httpHelper("RUNTIME", "POST", "security/login2FA", data || {});
			if (!auth.error) {
				const { tokens, user } = auth.response;
				Vue.$cookies.set("refreshToken", tokens.refresh.token, tokens.refresh.expires);
				Vue.$cookies.set("refreshTokenExpiry", tokens.refresh.expires, tokens.refresh.expires);
				Vue.$cookies.set("accessToken", tokens.access.token, tokens.access.expires);
				Vue.$cookies.set("email", user.email, "60d");
				Vue.$cookies.set("username", user.username, "60d");
				Vue.$cookies.set("orgName", user.organisationName, "60d");
				if (user?.workflowTourStatus) {
					Vue.$cookies.set("workflowTourStatus", user.workflowTourStatus, "60d");
				}
				commit("logIn2FA", { tokens, user });
			} else {
				commit("logOut");
				return auth;
			}
		},
		async logOutMETHOD({ commit }) {
			await httpHelper("RUNTIME", "POST", "security/logout", {
				refreshToken: Vue.$cookies.get("refreshToken"),
			});
			commit("logOut");
		},
		async returnSpecificWorkflowMETHOD({ commit }, data) {
			const result = await httpHelper("RUNTIME", "GET", "workflow/returnOne", data);
			if (!result?.error) {
				commit("setActiveWorkflow", result.response);
			} else {
				commit("setActiveWorkflow", null);
			}
			return result.response;
		},
		async fetchWorkflowVersionsMETHOD(_, workflowId) {
			const result = await httpHelper(
				"RUNTIME",
				"GET",
				`version/getAllVersions/${workflowId}`
			);

			if (!result?.error) {
				return result.response.versions || [];
			}

			return [];
		},
		async deleteVersion({ commit }, data) {
			const result = await httpHelper(
				"RUNTIME",
				"DELETE",
				`version/deleteVersion/${data.versionId}`
			);

			if (!result?.error) {
				commit("updateSelectedVersions", { workflowId: data.workflowId, versionId: data.versionId });
				return { success: true };
			}

			return {};
		},
		updateReferencePathMETHOD({ commit }, path) {
			commit("updateReferencePath", path);
		},
		updateLastFocusedVariableInsertionInputMETHOD({ commit }, componentId) {
			commit("updateLastFocusedVariableInsertionInput", componentId);
		},
		updateUnsavedChangesStateMETHOD({ commit }, unsavedChangesPresent) {
			commit("updateUnsavedChangesState", unsavedChangesPresent);
		},
		updateInputsOutputsForNodeMETHOD({ commit }, payload) {
			commit("updateInputsOutputsForNode", payload);
		},
		changesToUsageCreditMETHOD({ commit }) {
			commit("changesToUsageCredit", null);
		},
		changesToNodeSettingsMETHOD({ commit }, nodeId) {
			commit("changesToNodeSettings", nodeId);
		},
		toggleStickyNotesMETHOD({ commit }, value) {
			commit("SET_STICKY_NOTES_VISIBILITY", value);
		},
		updateStickyNoteDescriptionMETHOD({ commit }, text) {
			commit("SET_STICKY_NOTE_DESCRIPTION", text);
		},
		async fetchStickyNoteTemplatesMETHOD({ commit }, payload) {
			commit("SET_STICKY_NOTES_LOADING", true);

			try {
				const result = await httpHelper(
					"RUNTIME",
					"POST",
					"template/match",
					payload
				);

				if (!result?.error) {
					commit("SET_STICKY_NOTE_MATCHES", result.response.matches || []);
					commit("SET_STICKY_NOTE_TOTAL", result.response.totalRecords || 0);
				}
			} catch (e) {
				commit("SET_STICKY_NOTE_MATCHES", []);
			} finally {
				commit("SET_STICKY_NOTES_LOADING", false);
			}
		},
		async insertTemplateIntoWorkflowMETHOD(_, data) {
			try {
				const result = await httpHelper(
					"RUNTIME",
					"POST",
					"workflow/merge",
					{ workflowId: data?.workflowId, versionId: data?.versionId, templateWorkflowNodes: data?.template?.workflowNodes, workflowLastUpdated: data?.workflowLastUpdated }
				);

				if (!result?.error) {
					return result.response || [];
				}
			} catch (e) {
				return e;
			}
		},
		async addStickyNoteToWorkflowMETHOD({ state }, payload) {
			try {
				const result = await httpHelper(
					"RUNTIME",
					"POST",
					"workflow/sticky-note/add",
					{
						workflowId: payload.workflowId,
						versionId: payload.versionId,
						description: payload.description,
						color: payload.color,
						workflowLastUpdated: state.activeWorkflow?.updatedAt
					}
				);

				if (!result?.error) {
					return result.response;
				}
				return result.error;
			} catch (e) {
				return e;
			}
		},
		async updateStickyNoteInWorkflowMETHOD({ state }, payload) {
			try {
				const result = await httpHelper(
					"RUNTIME",
					"POST",
					"workflow/sticky-note/update",
					{
						workflowId: payload.workflowId,
						versionId: payload.versionId,
						nodeId: payload.nodeId,
						description: payload.description,
						color: payload.color,
						canvasPosition: payload.canvasPosition,
						workflowLastUpdated: payload?.workflowUpdatedAt ?? state.activeWorkflow?.updatedAt
					}
				);

				if (!result?.error) {
					return result.response;
				}
			} catch (e) {
				return e;
			}
		},
		async removeStickyNoteFromWorkflowMETHOD({ state }, payload) {
			try {
				const result = await httpHelper(
					"RUNTIME",
					"POST",
					"workflow/sticky-note/remove",
					{
						workflowId: payload.workflowId,
						versionId: payload.versionId,
						nodeId: payload.nodeId,
						workflowLastUpdated: payload?.workflowUpdatedAt ?? state.activeWorkflow?.updatedAt
					}
				);

				if (!result?.error) {
					return result.response;
				}
			} catch (e) {
				return e;
			}
		},
		async fetchAdminStatusMETHOD({ commit }) {
			try {
				const orgName = Vue.$cookies.get("orgName");
				const email = Vue.$cookies.get("email");

				const result = await httpHelper(
					"RUNTIME",
					"GET",
					"security/fetchActiveOrganisations",
					{}
				);

				const organisations = result.response || [];

				const organisation = organisations.find(
					(o) => o.organisationName === orgName
				);

				const isAdmin = !!(organisation && organisation.organisationAdminEmail === email);
				commit("SET_ADMIN", isAdmin);
				return isAdmin;
			} catch (e) {
				commit("SET_ADMIN", false);
				return false;
			}
		},
	},
	mutations: {
		logIn(state, data) {
			state.otpSent = true;
			state.userEmail = data.user.email;
			state.username = data.user.username;
			state.organisationName = data.user.organisationName;
		},
		logIn2FA(state, data) {
			state.userIsLoggedIn = true;
			state.userEmail = data.user.email;
			state.authToken = data.tokens.access.token;
			state.authTokenExpires = data.tokens.access.expires;
			state.username = data.user.username;
			state.organisationName = data.user.organisationName;
		},
		logOut(state) {
			Vue.$cookies.remove("refreshToken");
			Vue.$cookies.remove("refreshTokenExpiry");
			Vue.$cookies.remove("accessToken");
			Vue.$cookies.remove("accessTokenExpiry");
			Vue.$cookies.remove("email");
			state.userIsLoggedIn = false;
			state.authTokenExpires = null;
			state.authToken = null;
			localStorage.removeItem("workflow_selected_versions");
		},
		updateSelectedVersions(state, { workflowId, versionId }) {
			const stored =
				JSON.parse(localStorage.getItem("workflow_selected_versions")) || {};

			if (stored[workflowId] === versionId) {
				delete stored[workflowId];
			}

			state.selectedWorkflowVersions = stored;

			localStorage.setItem(
				"workflow_selected_versions",
				JSON.stringify(stored)
			);
		},
		setActiveWorkflow(state, data) {
			state.activeWorkflow = data;
		},
		updateReferencePath(state, path) {
			state.referencePath = { path: path };
		},
		updateLastFocusedVariableInsertionInput(state, componentId) {
			state.lastFocusedVariableInsertionInput = componentId;
		},
		updateUnsavedChangesState(state, unsavedChangesPresent) {
			state.unsavedChangesPresent = unsavedChangesPresent;
		},
		updateInputsOutputsForNode(state, payload) {
			state.inputsOutputsForNode = payload;
		},
		changesToUsageCredit(state) {
			state.changesToUsageCredit = !state.changesToUsageCredit;
		},
		changesToNodeSettings(state, nodeId) {
			state.changesToNodeSettings = nodeId;
		},
		SET_WORKFLOW_VERSION(state, { workflowId, versionId }) {
			state.selectedWorkflowVersions = {
				...state.selectedWorkflowVersions,
				[workflowId]: versionId
			};

			localStorage.setItem(
				"workflow_selected_versions",
				JSON.stringify(state.selectedWorkflowVersions)
			);
		},
		SET_STICKY_NOTES_VISIBILITY(state, value) {
			state.showStickyNotes = value;
		},
		SET_STICKY_NOTE_DESCRIPTION(state, text) {
			state.stickyNoteDescription = text;
		},
		SET_STICKY_NOTE_MATCHES(state, matches) {
			state.stickyNoteMatches = matches;
		},
		SET_STICKY_NOTES_LOADING(state, value) {
			state.stickyNotesLoading = value;
		},
		SET_STICKY_NOTE_TOTAL(state, total) {
			state.stickyNoteTotal = total;
		},
		SET_CANVAS_ZOOM(state, zoom) {
			state.canvasZoom = zoom;
		},
		setWalletBalance(state, balance) {
			state.walletBalanceGBP = balance;
		},
		SET_ADMIN(state, status) {
			state.isAdmin = status;
		}
	},
	strict: true,
});
